// Session clinical-codes API fns — carved verbatim from
// `src/api/scribeAPIV2.ts` (which now re-exports from here; Wave 2 · D2).
// Transport comes from the `@heidi/api` configured-client registry (E5,
// ARCFE-805) — the registry hands back the app's real `scribeHttpV2`
// singleton (same interceptors, same retry/refresh state), so behaviour is
// byte-identical. The structural client view keeps axios types out of this
// package's public surface.
//
// Naming: the hub's `searchClinicalCodes` collides with the SCC-service
// search in `clinical-coding-api.ts`, so the carved fn is
// `searchSessionClinicalCodes`; the hub re-exports it under its old name.
import { getScribeHttpClients } from '@heidi/api/client';
import type { CodeConcept, SessionClinicalCodesResponse } from '../../contracts/types';

interface ScribeHttp {
  get<T>(
    url: string,
    config?: {
      params?: Record<string, unknown>;
      paramsSerializer?: (
        params: Record<
          string,
          string | number | boolean | Array<string | number | boolean>
        >
      ) => string;
    }
  ): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
}

const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

const paramsSerializer = (
  params: Record<string, string | number | boolean | Array<string | number | boolean>>
): string => {
  return Object.entries(params)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .map((val) => `${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`)
          .join('&');
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
    })
    .join('&');
};

export const getSessionClinicalCodes = (sessionId: string, forceRegenerate?: boolean) => {
  return http().get<SessionClinicalCodesResponse>(
    `/sessions/${encodeURIComponent(sessionId)}/clinical-codes`,
    {
      params: forceRegenerate ? { force_regenerate: true } : undefined,
    }
  );
};

export const addNewClinicalCode = (
  sessionId: string,
  code: string,
  codeSystem: string
) => {
  return http().post(`/sessions/${sessionId}/clinical-codes/add-new-code`, {
    code,
    code_system: codeSystem,
  });
};

export const confirmClinicalCode = (input: {
  sessionId: string;
  codes: {
    code: string;
    code_system: string;
    entity_id: string;
  }[];
}) => {
  const { sessionId, codes } = input;
  return http().post(`/sessions/${sessionId}/clinical-codes/confirm`, {
    codes,
  });
};

export const rejectClinicalCode = (sessionId: string, entityId: string) => {
  return http().post(`/sessions/${sessionId}/clinical-codes/${entityId}/reject`);
};

export const unconfirmClinicalCode = (input: {
  sessionId: string;
  entity_ids: string[];
}) => {
  const { sessionId, entity_ids } = input;
  return http().post(`/sessions/${sessionId}/clinical-codes/unconfirm`, {
    entity_ids,
  });
};

export const searchSessionClinicalCodes = (
  sessionId: string,
  query: string,
  codeSystems?: string[]
) => {
  // Create base params object with query
  const params: Record<string, string[] | string> = { query };
  // Only add code_systems if it's defined and not empty
  if (codeSystems && codeSystems.length > 0) {
    params.code_systems = codeSystems;
  }

  return http().get<{ codes: CodeConcept[] }>(
    `/sessions/${sessionId}/clinical-codes/search`,
    {
      params,
      paramsSerializer: (params) => {
        return paramsSerializer(params);
      },
    }
  );
};

export const resetClinicalCodesQuota = () => {
  return http().post('/user/quota/clinical-code-auto-gen/reset');
};
